"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import AnswerBox from "@/components/shared/AnswerBox";
import ApoBankPartnerSection from "@/components/shared/ApoBankPartnerSection";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { CTABanner } from "@/components/shared/CTABanner";
import { EASE, fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { teamMembers } from "@/lib/team";

const milestones = [
  { year: "2019", label: "Gründung", desc: "ncrechner startet als einfacher Webrechner in einem Studien-Forum." },
  { year: "2020", label: "Erste App", desc: "Launch der ersten iOS- und Android-App mit allen öffentlichen Unis." },
  { year: "2022", label: "TMS-Rechner", desc: "Neues Tool mit Werner Zurowetz von tmsbuddies für die TMS-Planung." },
  { year: "2024", label: "Heute", desc: "Tausende Nutzer, alle Quoten, stetig aktuelle Zulassungsdaten." },
];

const partners = [
  {
    name: "future-doctor.de",
    description: "Dein Leitfaden ins Medizinstudium: Tipps, Guides und Ressourcen für angehende Medizinstudenten.",
    href: "https://www.future-doctor.de",
  },
  {
    name: "travel4med",
    description: "Spezialist für das Medizinstudium im Ausland mit Beratung für Studienplätze in Europa.",
    href: "https://travel4med.de",
  },
  {
    name: "tmsbuddies",
    description: "Gemeinsam haben wir den TMS-Rechner entwickelt. Werner Zurowetz brachte das Fachwissen ein.",
    href: "https://tmsbuddies.de",
  },
];

export default function UeberUnsContent() {
  return (
    <>
      <PageHeader
        eyebrow="Über uns"
        title={
          <>
            Drei Studenten.
            <br />
            Eine <span className="text-accent">Mission</span>.
          </>
        }
        subtitle="Drei Medizinstudenten, eine App und die Mission, den Weg ins Studium für alle transparent und kostenlos zu machen."
      />

      {/* ── AEO-Kurzantwort: Wer wir sind ── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnswerBox
          question="Wer steckt hinter ncrechner?"
          answer="ncrechner ist aus einem Problem entstanden, das wir selbst nur zu gut kannten: Wie kommt man ins Medizinstudium, wenn die Abiturnote allein nicht reicht?"
          body={[
            "2019 standen Nils, Lennart und Arwin genau vor dieser Frage. Alle drei wollten Medizin studieren und mussten sich selbst durch TMS, Praktika, Auswahlquoten und die unterschiedlichen Zulassungsverfahren der Universitäten kämpfen. Am Ende war es vor allem der TMS, der ihnen den Weg ins Medizinstudium ermöglicht hat.",
            "Aus dieser Erfahrung entstand die Idee für ncrechner: ein Tool, das anderen Studieninteressierten genau das ersparen sollte – stundenlange Recherche, komplizierte Auswahlsatzungen und die Frage, an welcher Universität man mit den eigenen Voraussetzungen eigentlich eine realistische Chance hat.",
            "Was als Projekt von drei Freunden begann, wurde über die Jahre gemeinsam mit einem großen Team aus Studierenden kontinuierlich weiterentwickelt. Heute bildet ncrechner die Zulassungswege der öffentlichen medizinischen Hochschulen in Deutschland ab und wird laufend an neue Auswahlverfahren und Grenzwerte angepasst.",
          ]}
          keyTakeaways={[
            "2019 aus eigener Erfahrung gegründet – von Nils, Lennart und Arwin auf ihrem eigenen Weg ins Medizinstudium.",
            "Vom eigenen Zulassungsproblem zur App – TMS, Praktika und Auswahlverfahren waren für die Gründer selbst entscheidend.",
            "Gemeinsam weiterentwickelt – heute arbeitet ein größeres Team aus Studierenden kontinuierlich daran, ncrechner noch besser zu machen.",
            "Kostenlos & ohne Registrierung – mit öffentlichen Hochschulen, Vergabequoten und individuellen Zulassungschancen.",
            "Laufend aktualisiert – anhand der offiziellen Auswahlsatzungen und Daten von Hochschulstart.",
          ]}
        />
      </div>

      {/* ── Founding story ── */}
      <section className="bg-paper py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
            <motion.div
              variants={stagger(0.09)}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="lg:col-span-6 flex flex-col gap-6"
            >
              <motion.p
                variants={fadeUp}
                className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint"
              >
                <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
                Wie alles begann
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-[1.1] text-ink"
              >
                Kennengelernt haben wir uns in einem Medizin-Forum.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed text-lg">
                Als wir uns selbst für das Medizinstudium bewarben, merkten wir
                schnell: Das Zulassungsverfahren ist unglaublich komplex.
                Verschiedene Quoten, unterschiedliche Berechnungsmethoden,
                unzählige Hochschulen. Und keine einzige App, die das alles
                übersichtlich zusammenfasst.
              </motion.p>
              <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed">
                Also haben wir ncrechner selbst gebaut. Angefangen als kleines
                Hobbyprojekt, ist die App über die Jahre zu einer zuverlässigen
                Ressource für tausende Medizinstudenten geworden. Heute umfasst
                sie alle öffentlichen deutschen Hochschulen und alle relevanten
                Zulassungsquoten.
              </motion.p>
              <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed">
                Und das Beste: ncrechner ist und bleibt{" "}
                <span className="font-semibold text-accent">kostenlos</span>. Immer.
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.7, ease: EASE }}
              className="lg:col-span-6 relative"
            >
              <div className="absolute inset-x-6 top-6 -bottom-6 bg-accent-soft rounded-[2rem] rotate-2" aria-hidden="true" />
              <Image
                src="/images/app-mockup-old.jpg"
                alt="Erste Version der ncrechner App"
                width={560}
                height={420}
                className="relative rounded-2xl border border-line w-full h-auto object-cover"
              />
              <div className="absolute -bottom-4 right-6 bg-paper border border-line rounded-xl px-4 py-3 shadow-sm">
                <span className="block text-xs font-bold uppercase tracking-wider text-ink-faint">Gegründet</span>
                <span className="block mt-1 font-display font-bold text-xl text-ink tnum">2019</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Milestones ── */}
      <section className="bg-paper py-24 lg:py-28 border-t border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge="Unsere Reise"
            title="Vom Hobbyprojekt zur meistgenutzten NC-App"
            subtitle="Von einem kleinen Webrechner zur Anlaufstelle für tausende Bewerberinnen und Bewerber."
          />

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4">
            {milestones.map(({ year, label, desc }, i) => (
              <motion.div
                key={year}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.6, delay: i * 0.07, ease: EASE }}
                className="py-8 lg:pr-10 lg:[&:not(:first-child)]:pl-10"
              >
                <span className="font-display font-bold text-4xl text-accent tnum leading-none">
                  {year}
                </span>
                <h3 className="font-display font-bold text-lg text-ink mt-4">{label}</h3>
                <p className="text-ink-soft text-sm leading-relaxed mt-2">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="bg-paper-deep py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge="Das Team"
            title="Drei Köpfe hinter ncrechner"
            subtitle="Drei unterschiedliche Wege, eine gemeinsame Idee: den Weg ins Medizinstudium einfacher und transparenter zu machen."
          />

          <div className="mt-16 grid md:grid-cols-3 gap-12 lg:gap-16">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                className="flex flex-col items-center text-center"
              >
                <Image
                  src={member.image}
                  alt={member.name}
                  width={120}
                  height={120}
                  className="w-28 h-28 rounded-full object-cover border border-line"
                />
                <h3 className="font-display font-bold text-xl text-ink mt-6">{member.name}</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-accent mt-2">
                  {member.role}
                </p>
                <p className="text-ink-soft text-sm leading-relaxed mt-4 max-w-xs">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="bg-accent text-paper py-24 lg:py-28">
        <motion.div
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl mx-auto px-4 sm:px-6 flex flex-col items-center text-center gap-6"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs font-bold uppercase tracking-widest text-paper/60"
          >
            Unsere Mission
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1]"
          >
            Eine Community.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-paper/80 text-lg leading-relaxed">
            Wir entwickeln ncrechner nicht für Profit. Wir entwickeln es, weil
            wir wissen, wie stressig und undurchsichtig das Bewerbungsverfahren
            für Medizinstudienplätze sein kann.
          </motion.p>
          <motion.p variants={fadeUp} className="text-paper/80 text-lg leading-relaxed">
            ncrechner ist unser Beitrag dazu, diesen Prozess ein bisschen
            einfacher, transparenter und zugänglicher zu machen. Für jeden, der
            seinen Traum vom Medizinstudium lebt.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Partners ── */}
      <section className="bg-paper py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge="Unsere Partner"
            title="Starke Partner an unserer Seite"
            subtitle="Gemeinsam bieten wir dir das beste Rundum-Paket für dein Medizinstudium."
          />

          <div className="mt-14">
            {partners.map(({ name, description, href }, i) => (
              <motion.a
                key={name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportOnce}
                transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start sm:items-center gap-6 py-6 border-t border-line last:border-b cursor-pointer"
              >
                <span className="font-display font-semibold text-accent tnum text-lg leading-none pt-1 sm:pt-0 w-8 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className="block font-display font-bold text-lg text-ink group-hover:text-accent transition-colors duration-200">
                    {name}
                  </span>
                  <span className="block text-ink-soft text-sm leading-relaxed mt-1">{description}</span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-ink-faint group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200 shrink-0"
                  aria-hidden="true"
                />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ── apoBank-Kooperation (Anzeige) ── */}
      <div className="bg-paper pb-8">
        <ApoBankPartnerSection />
      </div>

      <CTABanner
        title="Jetzt starten."
        text="Lade ncrechner kostenlos herunter und berechne deine Zulassungschancen in weniger als 2 Minuten."
      />
    </>
  );
}
